import { FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { login } from '../lib/auth'
import { SESSION_EXPIRED_KEY } from '../lib/adminClient'

export default function Login() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Whether we landed here because a session expired mid-use. The read in the
  // initializer is pure; the flag is cleared in the effect below rather than
  // here, because React strict mode double-invokes the initializer and a clear
  // in it would wipe the flag before the retained render could show the notice.
  const [expired] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SESSION_EXPIRED_KEY) === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      if (localStorage.getItem(SESSION_EXPIRED_KEY) === '1') {
        localStorage.removeItem(SESSION_EXPIRED_KEY)
      }
    } catch {
      // ignore storage failures
    }
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const session = await login(email, password)
      if (!session) {
        setError('Invalid email or password.')
        return
      }
      navigate('/', { replace: true })
    } catch (err) {
      setError(
        err instanceof Error && err.message.toLowerCase().includes('failed to fetch')
          ? 'Could not reach the Plexii server. Make sure focusbuddy-signal is running on http://localhost:3001.'
          : 'Unexpected error during sign-in.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm glass-card rounded-2xl p-8">
        <div className="text-[11px] tracking-[0.3em] text-slate-400 uppercase mb-1">Plexii</div>
        <h1 className="text-xl font-semibold mb-1">Admin console</h1>
        <p className="text-sm text-slate-400 mb-6">Sign in with your admin account.</p>
        {expired && (
          <div
            data-testid="session-expired-notice"
            className="text-xs text-amber-300 bg-amber-400/10 border border-amber-400/20 rounded-md px-3 py-2 mb-4"
          >
            Your admin session expired, so changes were not being saved. Sign in again to continue, and your edits will persist.
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Email</label>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
              placeholder="you@plexiidesk.com"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Password</label>
            <input
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
            />
          </div>
          {error && <div className="text-xs text-red-400">{error}</div>}
          <button type="submit" className="btn-primary w-full justify-center" style={{ display: 'flex' }} disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
